// SCR-STAFF-951 — قالب‌های پیام (US-MSG-010، 012؛ AP-MSG-01): فهرست قالب‌ها با هدف، نسخه و طول بدترین حالت؛ ویرایشگر با فهرست سفید
// متغیرها (درج با کلیک)، پیش‌نمایش با داده نمونه و شمارش نویسه و بخش UCS-2 (۷۰ و ۶۷ — BR-MSG-012)، متن خنثی دسته حساس (BR-MSG-013) و
// بازگشت به متن سیستم. متغیر خارج از فهرست ← PHX-MSG-0101 و متغیر بالینی ← PHX-MSG-0102 کنار فیلد. AP-MSG-02 (US-MSG-011): نسخه فارسی،
// انگلیسی و عربی هر قالب؛ زبان بدون نسخه با نشان «فارسی ارسال می‌شود» (AC-MSG-011.1).
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { unwrap, type MessageTemplateItem, type SmsLengthView } from '@phoenix/api-client';
import { useTenantSession, useWriteAccess } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import { DataTable, ErrorState, faDigits, FormDialog, ListPage, type DataTableColumn } from '@phoenix/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { WriteNotice } from '../settings/common';
import { msgKeys, SCR_STAFF_951, usePurposeLabel, useTemplateLabel } from './common';

function LengthText({ length, testId }: { length: SmsLengthView; testId?: string }) {
  const { t } = useTranslation('msg');
  return (
    <span data-testid={testId} data-parts={String(length.parts)}>
      {t('msg:templates.length', {
        characters: faDigits(String(length.characters)),
        parts: faDigits(String(length.parts)),
      })}
    </span>
  );
}

export const TEMPLATE_LANGUAGES = ['fa', 'en', 'ar'] as const;
export type TemplateLanguage = (typeof TEMPLATE_LANGUAGES)[number];

function TemplateEditor({
  template,
  language,
  onClose,
}: {
  template: MessageTemplateItem;
  language: TemplateLanguage;
  onClose: () => void;
}) {
  const { t } = useTranslation('msg');
  const { tenantApi, session } = useTenantSession();
  const queryClient = useQueryClient();
  const [body, setBody] = useState(template.body);
  const [sensitive, setSensitive] = useState(false);
  const input = useRef<HTMLTextAreaElement | null>(null);
  const path = { params: { path: { code: template.code }, query: { language } } };

  const preview = useMutation({
    mutationFn: (request: { body: string; sensitive: boolean }) =>
      unwrap(tenantApi.POST('/api/v1/messaging/templates/{code}:preview', { ...path, body: request })),
  });
  const { mutate: runPreview } = preview;
  useEffect(() => {
    const handle = setTimeout(() => runPreview({ body, sensitive }), 300);
    return () => clearTimeout(handle);
  }, [body, sensitive, runPreview]);

  const invalidate = () =>
    void queryClient.invalidateQueries({ queryKey: ['messaging', 'templates', session?.tenantId] });
  const save = useMutation({
    mutationFn: () =>
      unwrap(tenantApi.PUT('/api/v1/messaging/templates/{code}', { ...path, body: { body } })),
    onSuccess: () => {
      invalidate();
      onClose();
    },
  });
  const reset = useMutation({
    mutationFn: () => unwrap(tenantApi.POST('/api/v1/messaging/templates/{code}:reset', path)),
    onSuccess: () => {
      invalidate();
      onClose();
    },
  });

  const insert = (variable: string) => {
    const element = input.current;
    const token = `{${variable}}`;
    if (!element) {
      setBody((current) => current + token);
      return;
    }
    const start = element.selectionStart ?? body.length;
    const end = element.selectionEnd ?? body.length;
    setBody(body.slice(0, start) + token + body.slice(end));
  };

  return (
    <FormDialog
      open
      titleKey="msg:templates.edit_title"
      screenId={SCR_STAFF_951}
      onSubmit={() => save.mutate()}
      onClose={onClose}
      isDirty={body !== template.body}
      isSubmitting={save.isPending || reset.isPending}
      submitError={save.error ?? reset.error}
      maxWidth="md"
    >
      <Typography data-testid="template-editor-code" data-language={language}>
        <bdi dir="ltr">{template.code}</bdi> — {t(`msg:templates.languages.${language}`)}
      </Typography>
      <TextField
        multiline
        minRows={4}
        label={t('msg:templates.body')}
        value={body}
        onChange={(event) => setBody(event.target.value)}
        inputRef={input}
        slotProps={{ htmlInput: { 'data-testid': 'template-body', dir: language === 'en' ? 'ltr' : 'rtl' } }}
      />
      <Stack
        direction="row"
        spacing={0.5}
        sx={{ flexWrap: 'wrap', rowGap: 0.5 }}
        aria-label={t('msg:templates.variables')}
      >
        {template.variables.map((variable) => (
          <Chip
            key={variable}
            label={`{${variable}}`}
            onClick={() => insert(variable)}
            size="small"
            data-testid={`template-variable-${variable}`}
          />
        ))}
      </Stack>
      <Typography color="text.secondary" variant="body2">
        {t('msg:templates.clinical_hint')}
      </Typography>
      {template.hasNeutralVariant ? (
        <Button
          size="small"
          onClick={() => setSensitive((value) => !value)}
          data-testid="template-preview-sensitive"
          sx={{ alignSelf: 'flex-start' }}
        >
          {t(sensitive ? 'msg:templates.preview_normal' : 'msg:templates.preview_sensitive')}
        </Button>
      ) : null}
      <Paper variant="outlined" sx={{ p: 1.5 }} data-testid="template-preview">
        <Typography variant="subtitle2">{t('msg:templates.preview')}</Typography>
        {preview.data ? (
          <Stack spacing={0.5}>
            <Typography sx={{ whiteSpace: 'pre-wrap' }} data-testid="template-preview-text">
              {preview.data.text}
            </Typography>
            <Typography variant="body2">
              {t('msg:templates.sample')}:{' '}
              <LengthText length={preview.data.sample} testId="template-preview-sample" />
            </Typography>
            <Typography variant="body2">
              {t('msg:templates.worst_case')}:{' '}
              <LengthText length={preview.data.worstCase} testId="template-preview-worst" />
            </Typography>
          </Stack>
        ) : null}
        {preview.error ? <ErrorState error={preview.error} compact /> : null}
      </Paper>
      {template.customized ? (
        <Button
          color="warning"
          onClick={() => reset.mutate()}
          disabled={reset.isPending}
          data-testid="template-reset"
          sx={{ alignSelf: 'flex-start' }}
        >
          {t('msg:templates.reset')}
        </Button>
      ) : null}
    </FormDialog>
  );
}

export function TemplatesPage() {
  const { t } = useTranslation('msg');
  const { tenantApi, session } = useTenantSession();
  const write = useWriteAccess('msg.template.manage');
  const purposeLabel = usePurposeLabel();
  const templateLabel = useTemplateLabel();
  const [editing, setEditing] = useState<MessageTemplateItem | null>(null);
  const [language, setLanguage] = useState<TemplateLanguage>('fa');
  const templates = useQuery({
    queryKey: msgKeys.templates(session?.tenantId, language),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/messaging/templates', { params: { query: { language } } })),
  });

  const columns: DataTableColumn<MessageTemplateItem>[] = [
    {
      id: 'name',
      headerKey: 'msg:templates.columns.name',
      cell: (row) => (
        <Stack spacing={0.25}>
          <span>{templateLabel(row.code)}</span>
          <Typography variant="caption" color="text.secondary">
            <bdi dir="ltr">{row.code}</bdi>
          </Typography>
        </Stack>
      ),
    },
    { id: 'purpose', headerKey: 'msg:templates.columns.purpose', cell: (row) => purposeLabel(row.purpose) },
    {
      id: 'version',
      headerKey: 'msg:templates.columns.version',
      cell: (row) => (
        <Stack spacing={0.25}>
          <span
            data-testid={`template-version-${row.code}`}
            data-customized={row.customized ? 'true' : 'false'}
          >
            {row.customized
              ? t('msg:templates.custom_version', { version: faDigits(String(row.version)) })
              : t('msg:templates.system')}
          </span>
          {row.available === false ? (
            <Chip
              size="small"
              color="warning"
              variant="outlined"
              label={t('msg:templates.no_version')}
              data-testid={`template-no-version-${row.code}`}
            />
          ) : null}
        </Stack>
      ),
    },
    {
      id: 'length',
      headerKey: 'msg:templates.columns.length',
      cell: (row) => <LengthText length={row.length} />,
    },
    {
      id: 'flags',
      headerKey: 'msg:templates.columns.flags',
      cell: (row) => (
        <Stack direction="row" spacing={0.5}>
          {row.requiresReply ? <Chip size="small" label={t('msg:templates.requires_reply')} /> : null}
          {row.hasNeutralVariant ? <Chip size="small" label={t('msg:templates.neutral')} /> : null}
        </Stack>
      ),
    },
    {
      id: 'actions',
      headerKey: 'msg:templates.columns.actions',
      cell: (row) =>
        row.editable ? (
          <Tooltip title={t('msg:templates.edit')}>
            <span>
              <IconButton
                aria-label={t('msg:templates.edit')}
                onClick={() => setEditing(row)}
                disabled={!write.canWrite}
                data-testid={`template-edit-${row.code}`}
              >
                <EditOutlinedIcon />
              </IconButton>
            </span>
          </Tooltip>
        ) : (
          <Typography variant="caption" color="text.secondary">
            {t('msg:templates.locked')}
          </Typography>
        ),
    },
  ];

  return (
    <ListPage
      screenId={SCR_STAFF_951}
      titleKey="msg:templates.title"
      subtitleKey="msg:templates.subtitle"
      filters={
        <Stack spacing={1}>
          <WriteNotice write={write} />
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
            <Typography variant="body2">{t('msg:templates.language')}</Typography>
            <ToggleButtonGroup
              exclusive
              size="small"
              value={language}
              onChange={(_, value: TemplateLanguage | null) => value && setLanguage(value)}
              aria-label={t('msg:templates.language')}
            >
              {TEMPLATE_LANGUAGES.map((code) => (
                <ToggleButton key={code} value={code} data-testid={`template-language-${code}`}>
                  {t(`msg:templates.languages.${code}`)}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Stack>
          <Alert severity="info">{t('msg:templates.language_hint')}</Alert>
          <Alert severity="info">{t('msg:templates.sensitive_hint')}</Alert>
        </Stack>
      }
    >
      <DataTable
        captionKey="msg:templates.caption"
        columns={columns}
        rows={templates.data?.items}
        getRowId={(row) => row.code}
        isLoading={templates.isPending}
        error={templates.error}
        onRetry={() => void templates.refetch()}
        emptyTitleKey="msg:templates.empty"
      />
      {editing ? (
        <TemplateEditor template={editing} language={language} onClose={() => setEditing(null)} />
      ) : null}
    </ListPage>
  );
}
